import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { AnimatedIcon, iconToneClass, useAnimatedIconState } from "@/components/internal/AnimatedIcon";

const SidebarContext = createContext({
    expanded: false,
    toggleExpanded: () => { },
    mobileOpen: false,
    setMobileOpen: () => { },
});

export function useSidebar() {
    return useContext(SidebarContext);
}

export function Sidebar({ children, forceCollapsed = false, mobileHeader }) {
    // Toggle por clique — sem hover, sem lock, sem instabilidade
    const [expanded, setExpanded] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggleExpanded = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    // Close mobile menu on resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setMobileOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isExpanded = forceCollapsed ? false : (mobileOpen ? true : expanded);

    return (
        <SidebarContext.Provider value={{ expanded: isExpanded, toggleExpanded, forceCollapsed, mobileOpen, setMobileOpen }}>
            {/* Mobile Header (rendered outside aside to always be visible on mobile) */}
            {mobileHeader}

            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                data-collapsed={!isExpanded}
                className={cn(
                    "group h-screen transition-all duration-300 border-r border-sidebar-border bg-sidebar flex flex-col z-50 fixed left-0 top-0",
                    isExpanded ? "w-64" : "w-16",
                    "hidden md:flex",
                    mobileOpen && "!flex w-64"
                )}
            >
                {children}
            </aside>

            {/* Spacer — acompanha o estado real da sidebar */}
            <div className={cn("transition-all duration-300 hidden md:block", isExpanded ? "w-64" : "w-16")} />
        </SidebarContext.Provider>
    );
}

/**
 * Botão de toggle para expandir/colapsar a sidebar.
 * Renderiza no footer da sidebar automaticamente.
 */
export function SidebarToggle() {
    const { expanded, toggleExpanded, forceCollapsed } = useContext(SidebarContext);
    const { hovered, playSignal, handlers } = useAnimatedIconState(false);

    if (forceCollapsed) return null;

    const icon = (
        <AnimatedIcon
            name="panel"
            fallback={expanded ? PanelLeftClose : PanelLeft}
            playSignal={playSignal}
            colorKey={String(hovered)}
            className={cn("h-5 w-5", iconToneClass({ hovered }))}
        />
    );

    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={toggleExpanded}
                        {...handlers}
                        className={cn(
                            "flex items-center rounded-lg px-3 py-2 cursor-pointer transition-colors text-foreground-muted hover:bg-sidebar-hover hover:text-foreground w-full",
                            !expanded && "justify-center px-2"
                        )}
                    >
                        {expanded ? (
                            <>
                                {icon}
                                <span className="font-medium ml-3 text-sm">Recolher</span>
                            </>
                        ) : (
                            icon
                        )}
                    </button>
                </TooltipTrigger>
                {!expanded && (
                    <TooltipContent side="right" sideOffset={8} className="bg-surface border-border-strong text-foreground-strong z-[9999]">
                        Expandir menu
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
}

// Header should just have the logo, no text
export function SidebarHeader({ children, className }) {
    const { expanded } = useContext(SidebarContext);

    // If children are provided (like the AccountSwitcher), render them.
    // However, if the sidebar is collapsed, we might want to force the logo view or render the children in a simplified way.
    // For now, let's assume the passed children (AccountSwitcher) handles its own responsive/collapsed state or we just return children.

    if (children) {
        return (
            <div className={cn("h-16 flex items-center border-b border-sidebar-border", className)}>
                {children}
            </div>
        );
    }

    return (
        <div className={cn("h-16 flex items-center justify-center border-b border-sidebar-border", className)}>
            <div className="w-8 h-8 rounded-lg bg-surface-active flex items-center justify-center shrink-0 shadow-sm border border-border-strong">
                <Lock className="text-foreground-muted w-4 h-4" />
            </div>
        </div>
    );
}

export function SidebarContent({ children }) {
    return (
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 flex flex-col gap-2 px-2 custom-scrollbar">
            {children}
        </div>
    );
}

export function SidebarFooter({ children }) {
    return (
        <div className="p-2">
            {children}
        </div>
    );
}

/**
 * O item ativo é preenchido; os outros são contorno.
 *
 * O Lucide é um conjunto só de traço — não existe variante sólida, e mandar
 * `fill` sozinho não resolve: Kanban e Relatórios são linhas abertas, sem área
 * para preencher, e ficariam idênticos ao estado inativo enquanto os vizinhos
 * mudavam. Por isso o preenchimento vem em três camadas que funcionam em
 * qualquer glifo — o fundo do item, o traço mais grosso e um `fill` de baixa
 * opacidade que só as formas fechadas aproveitam.
 *
 * A borda existe nos DOIS estados (transparente quando inativo) porque ela
 * ocupa 1px: só no ativo, o item pulava um pixel ao ser selecionado.
 *
 * Com `lottie="<nome>"` o item usa o ícone de contorno animado (ver
 * AnimatedIcon) e o `icon` do Lucide vira só o substituto até ele carregar. Esse
 * ícone não tem miolo nem espessura que mudem: o estado ativo se marca pelo
 * fundo, pela cor cyan e pela animação que toca na chegada.
 *
 * Com `navKey` (a chave do item em `NAV_ITEMS`) o item recebe `data-nav-key`: é nele
 * que o cartão de novidade (feature spotlight) se ancora. Fica no `div` interno, o
 * que tem borda e raio, para o cartão apontar para o item e não para a linha toda.
 */
const ICON_FILL_OPACITY = 0.18;

/**
 * `locked`: o módulo não está no plano (modo Otto). O item continua sendo um LINK real para o endereço do módulo (onde a
 * vitrine aparece), com o cadeado à vista e o nome acessível dizendo que não está incluso; `hint` é a dica lida por
 * `aria-describedby`. Sem `locked`, o item é exatamente o de sempre.
 */
export function SidebarItem({ icon: Icon, lottie, label, to, active, alert, onClick, className, navKey, locked = false, hint }) {
    const { expanded } = useContext(SidebarContext);
    const hintId = locked && hint ? `nav-hint-${navKey ?? label}` : undefined;
    const { hovered, playSignal, handlers } = useAnimatedIconState(Boolean(active));

    const content = (
        <div
            {...(lottie ? handlers : {})}
            data-nav-key={navKey}
            className={cn(
                "group/nav flex items-center rounded-lg px-3 py-2 cursor-pointer relative",
                "border transition-colors duration-200 ease-out",
                active
                    ? "bg-cyan-500/10 border-cyan-500/25 text-cyan-700 dark:text-cyan-300 shadow-sm"
                    : "border-transparent text-foreground-muted hover:bg-sidebar-hover hover:text-foreground",
                !expanded && "justify-center px-2",
                className
            )}
        >
            {lottie ? (
                // Ícone animado (contorno): a cor vem do texto do item e a animação
                // toca no hover e na chegada. Ver AnimatedIcon.
                <AnimatedIcon
                    name={lottie}
                    fallback={Icon}
                    playSignal={playSignal}
                    colorKey={`${Boolean(active)}-${hovered}`}
                    className={cn("h-5 w-5", iconToneClass({ active, hovered }))}
                />
            ) : (
                <Icon
                    className={cn(
                        "w-4 h-4 shrink-0 transition-transform duration-200 ease-out",
                        active ? "animate-nav-icon-pop" : "group-hover/nav:scale-110"
                    )}
                    strokeWidth={active ? 2.25 : 2}
                    fill={active ? "currentColor" : "none"}
                    fillOpacity={active ? ICON_FILL_OPACITY : 0}
                />
            )}

            <span className={cn(
                "font-medium transition-all duration-300 overflow-hidden whitespace-nowrap",
                expanded ? "opacity-100 max-w-full ml-3" : "opacity-0 max-w-0"
            )}>
                {label}
            </span>

            {locked && (
                <Lock
                    data-testid="nav-lock"
                    aria-hidden="true"
                    className={cn(
                        "shrink-0 text-foreground-muted",
                        expanded ? "ml-auto h-3.5 w-3.5" : "absolute bottom-0.5 right-0.5 h-2.5 w-2.5",
                    )}
                />
            )}
        </div>
    );

    // Tooltip do estado recolhido. A árvore é a mesma nos dois estados (só o conteúdo do
    // tooltip some quando expandido): trocar o pai remontaria o item, e com ele o player
    // do ícone animado, que recarregaria e piscaria o ícone de reserva a cada expansão.
    const wrappedContent = (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    {content}
                </TooltipTrigger>
                {!expanded && (
                    <TooltipContent side="right" sideOffset={8} className="bg-surface border-border-strong text-foreground-strong z-[9999]">
                        {label}
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );

    if (onClick) {
        return <div onClick={onClick} className="w-full text-left bg-transparent border-0">{wrappedContent}</div>;
    }

    return (
        <Link
            to={to}
            aria-label={locked ? `${label}, não incluso no seu plano` : undefined}
            aria-describedby={hintId}
        >
            {wrappedContent}
            {hintId && <span id={hintId} className="sr-only">{hint}</span>}
        </Link>
    );
}

// SidebarTrigger removido — sidebar expande apenas por hover, sem pin

/**
 * Wrapper around any component with onOpenChange (DropdownMenu, Popover, etc.)
 * A sidebar colapsa automaticamente ao sair do hover — menus são portals 
 * e permanecem visíveis independentemente.
 */
export function SidebarLock({ as: Component, onOpenChange, children, ...props }) {
    return <Component onOpenChange={onOpenChange} {...props}>{children}</Component>;
}

export default function InternalSidebar() {
    const navigate = useNavigate();
    const location = useLocation();

    // We define expanded state in the layout usage or internally here if we want to replace the whole Sidebar component
    // But for this custom implementation, let's just export the composition
    return null;
}
